// react
import { useCallback, useState } from 'react';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

// templates
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

export interface ReferringPartnerFormProps {
  title: string;
  initialName?: string;
  initialDomains?: string[];
  onBack: () => void;
  onSubmit: (name: string, domains: string[]) => void | Promise<void>;
  submitLabel?: string;
  isSubmitting?: boolean;
}

function parseDomains(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * ReferringPartnerForm collects partner name and email domains (comma- or space-separated) using base MUI.
 */
export const ReferringPartnerForm = ({
  title,
  initialName = '',
  initialDomains = [],
  onBack,
  onSubmit,
  submitLabel = 'Save',
  isSubmitting = false,
}: ReferringPartnerFormProps) => {
  const [name, setName] = useState(initialName);
  const [domainsInput, setDomainsInput] = useState(
    initialDomains.length > 0 ? initialDomains.join(', ') : '',
  );
  const [touched, setTouched] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched(true);
      if (!name.trim()) return;
      const domains = parseDomains(domainsInput);
      await onSubmit(name.trim(), domains);
    },
    [name, domainsInput, onSubmit],
  );

  const isValid = name.trim().length > 0;

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={<PageHeader title={title} />}
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label='Partner name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                autoComplete='organization'
                error={touched && !name.trim()}
                disabled={isSubmitting}
                helperText={touched && !name.trim() ? 'Required' : undefined}
              />
              <TextField
                label='Email domains (optional; add later if needed)'
                value={domainsInput}
                onChange={(e) => setDomainsInput(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                placeholder='e.g. sjpp.co.uk, example.com'
                disabled={isSubmitting}
                helperText='Comma- or space-separated list of email domains'
              />
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  type='button'
                  variant='outlined'
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={!isValid || isSubmitting}
                >
                  {submitLabel}
                </Button>
              </Stack>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

ReferringPartnerForm.displayName = 'ReferringPartnerForm';
export default ReferringPartnerForm;
